import type { RoomVisibility } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Button } from '../../components/Button';
import { GameHost } from '../../game-host/GameHost';
import { findClientGame } from '../../game-host/registry';
import { da } from '../../i18n/da';
import { useAuth } from '../auth/auth-context';
import { ChatBox } from '../chat/ChatBox';
import { FriendNotices } from '../friends/FriendNotices';
import { useFriends } from '../friends/useFriends';
import { parseRoomCode } from './room-code';
import { parseRestore } from './server-update';
import { RoomHud } from './RoomHud';
import { useRoomConnection, type RoomEntry } from './useRoomConnection';
import styles from './PlayPage.module.css';

export function PlayPage() {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();
  const game = findClientGame(gameId);
  // Read once: after creating a room we write its code into the URL without reconnecting.
  const [requestedCode] = useState(() => parseRoomCode(searchParams.get('room') ?? ''));
  const [entry] = useState(() => parseEntry(searchParams.get('mode')));
  const [restore] = useState(() => parseRestore(searchParams.get('restore')));
  const [friendId] = useState(() => searchParams.get('friend'));

  if (!game) {
    return <Message text={da.play.unknownGame} />;
  }
  return (
    <ConnectedGame
      gameId={game.id}
      code={requestedCode}
      friendId={requestedCode ? null : friendId}
      entry={entry}
      restore={restore}
    />
  );
}

/** `?mode=quick` is "play now", `?mode=private` a room only joined by code. Anything else is a new public room. */
function parseEntry(mode: string | null): RoomEntry {
  return mode === 'quick' || mode === 'private' ? mode : 'public';
}

interface ConnectedGameProps {
  gameId: string;
  code: string | null;
  friendId: string | null;
  entry: RoomEntry;
  restore: RoomVisibility | null;
}

/** In a room the friends list matters less than on the start page; changes come as pushes. */
const FRIENDS_REFRESH_MS = 20_000;

function ConnectedGame({ gameId, code, friendId, entry, restore }: ConnectedGameProps) {
  const game = findClientGame(gameId);
  const connection = useRoomConnection({ gameId, code, friendId, entry, restore });
  const friends = useFriends(FRIENDS_REFRESH_MS);
  const { refresh } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const [gameLoaded, setGameLoaded] = useState(false);

  const joinedCode = connection.status === 'joined' ? connection.code : null;
  useEffect(() => {
    if (joinedCode) setSearchParams({ room: joinedCode }, { replace: true });
  }, [joinedCode, setSearchParams]);

  // An expired session: asking again sends the player to the login page.
  const unauthorized = connection.status === 'unauthorized';
  useEffect(() => {
    if (unauthorized) refresh();
  }, [unauthorized, refresh]);

  switch (connection.status) {
    case 'failed':
      return <Message text={da.joinErrors[connection.error]} />;
    case 'replaced':
      return <Message text={da.play.replaced} />;
    case 'disconnected':
    case 'unauthorized':
      return <Message text={da.play.disconnected} />;
  }

  // Free text in public rooms needs the host's yes (ADR 0011).
  const joined = connection.status === 'joined' ? connection : null;
  const selfId = joined?.context.self.id ?? '';
  const publicRoom = joined?.visibility === 'public';
  const isHost = joined?.chatAccess.hostId === selfId;

  return (
    <main className={styles.page}>
      {connection.status === 'joined' && game && (
        <>
          <GameHost game={game} context={connection.context} onLoaded={() => setGameLoaded(true)} />
          <RoomHud
            code={connection.code}
            showCode={!publicRoom}
            players={connection.players}
            selfId={selfId}
            chatAccess={publicRoom ? connection.chatAccess : null}
            onRevokeChat={(playerId) => void connection.chat.decide(playerId, false)}
            onReport={async (playerId) => (await connection.chat.report(playerId)).ok}
            friends={friends}
            wallet={connection.wallet}
          >
            <ChatBox
              chat={connection.chat}
              selfId={selfId}
              canWrite={!publicRoom || isHost || connection.chatAccess.allowed.includes(selfId)}
              isHost={publicRoom && isHost}
            />
          </RoomHud>
          <FriendNotices friends={connection.friends} onChanged={friends.reload} />
          {(connection.reconnecting || connection.updating) && (
            <div className={styles.reconnecting} role="status">
              {connection.updating ? da.play.updating : da.play.reconnecting}
            </div>
          )}
        </>
      )}
      {(connection.status === 'connecting' || !gameLoaded) && (
        <div className={styles.loading} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {connection.status !== 'connecting'
            ? da.play.loadingGame
            : friendId && !code
              ? da.play.goingToFriend
              : da.play.connecting}
        </div>
      )}
    </main>
  );
}

function Message({ text }: { text: string }) {
  return (
    <main className={styles.messagePage}>
      <div className={styles.messageCard} role="alert">
        <p>{text}</p>
        {/* A full page load, not a route change: after a server update this is how an open tab
            gets the new code, so it does not keep talking to the server in an old way. */}
        <Button onClick={() => window.location.assign('/')}>{da.play.backToStart}</Button>
      </div>
    </main>
  );
}
